import type { AltYChatApi } from '../lib/useAltYChat';
import type { PinnedAgent } from '../lib/pinnedAgents';
import type { AltYCanvasContent, AltYEnvironmentTab } from '../types/global';
import { AltYPanel } from './AltYPanel';
import type { WorkspaceCanvasBadge } from './WorkspaceCanvas';

type AltYPanelHostProps = {
  isOpen: boolean;
  // Mobile fold: render the chat panel full-bleed with no env tabs / resize
  // handle. Passed straight through to AltYPanel.
  mobile?: boolean;
  chat: AltYChatApi;
  appFrame: HTMLIFrameElement | null;
  tabs: AltYEnvironmentTab[];
  activeTabId: string;
  onSelectTab: (tab: AltYEnvironmentTab) => void;
  pinnedAgents: PinnedAgent[];
  selectedAgentId: string;
  selectedPinnedAgentId: string | null;
  onSelectPinnedAgent: (id: string | null) => void;
  onEditPinnedAgent: (id: string) => void;
  onDeletePinnedAgent: (id: string) => void;
  onStartNewChat: () => void;
  onStartSubchat?: () => Promise<number | null> | number | null | void;
  onBrokerRestartReady?: () => Promise<void> | void;
  // Cog-menu "Improve agent": present (label set + handler wired) only when the
  // current chat is running a user-created agent that can be rewritten from it.
  improveAgentLabel?: string | null;
  onImproveAgent?: () => void;
  onShowApp?: () => void;
  canReopenDiagram?: boolean;
  onReopenDiagram?: () => void;
  // Idea Agent: when an Idea Agent is the active selection the chat header shows
  // a toggle that flips between the conversation and the saved ideas list.
  canShowIdeas?: boolean;
  ideasOpen?: boolean;
  onToggleIdeas?: () => void;
  // Apartment finder: same idea but for the saved apartment cards — a buildings
  // toggle in the header flips between the conversation and the cards list.
  canShowApartments?: boolean;
  apartmentsOpen?: boolean;
  onToggleApartments?: () => void;
  // Customized BMad Agent Flow: a clipboard toggle flips between the
  // conversation and the workflow tabs (story / rephrase / sources / gaps /
  // questions / design / architecture / development tasks).
  canShowBmadWorkflow?: boolean;
  bmadWorkflowOpen?: boolean;
  onToggleBmadWorkflow?: () => void;
  // Goal Discovery: a target toggle flips between the conversation and the
  // discovery brief (rephrase + four pieces, rendered as tabs).
  canShowDiscovery?: boolean;
  discoveryOpen?: boolean;
  onToggleDiscovery?: () => void;
  settingsOpen?: boolean;
  onSettingsOpenChange?: (open: boolean) => void;
  canvasOnly?: boolean;
  onCanvasOnlyChange?: (enabled: boolean) => void;
  canvasContent?: AltYCanvasContent | null;
  canvasBadge?: WorkspaceCanvasBadge;
  selectedUserIds?: string[];
  onSelectUserIds?: (userIds: string[]) => void;
};

export function AltYPanelHost(props: AltYPanelHostProps) {
  return (
    <div className="alty-panel-host" aria-live="polite">
      <AltYPanel {...props} />
    </div>
  );
}
